import React, { useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import { useCsvImport } from '../hooks/useCsvImport';

const UploadCsvCard = ({ orgId, userId, onComplete, disabled }) => {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState('dry'); // 'dry' | 'upsert'
  const [preview, setPreview] = useState({ headers: [], rows: [] });
  const [progress, setProgress] = useState({ total: 0, processed: 0, skipped: 0, done: false });
  const [busy, setBusy] = useState(false);
  const { importCsv, parsePreview } = useCsvImport(orgId, userId);

  const canStart = useMemo(() => !!file && !!orgId && !!userId && !busy, [file, orgId, userId, busy]);

  const handleChoose = async (e) => {
    const f = e?.target?.files?.[0];
    if (!f) return;
    setFile(f);
    const p = await parsePreview(f, 100);
    setPreview(p);
  };

  const handleStart = async () => {
    if (!canStart) return;
    setBusy(true);
    setProgress({ total: 0, processed: 0, skipped: 0, done: false });
    try {
      const summary = await importCsv(file, mode === 'dry', (p) => setProgress(prev => ({ ...prev, ...p })));
      onComplete?.(summary);
    } catch (e) {
      alert(e?.message || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-foreground">Upload EPOS CSV</h3>
          <p className="text-xs text-muted-foreground">Max 50 MB. Required headers: PLU, DESC, COST.</p>
        </div>
        <div className="flex items-center space-x-2">
          <select
            className="text-sm border border-border rounded px-2 py-1 bg-background"
            value={mode}
            onChange={(e) => setMode(e.target.value)}
          >
            <option value="dry">Dry Run</option>
            <option value="upsert">Upsert</option>
          </select>
          <input type="file" accept=".csv" onChange={handleChoose} />
          <Button variant="default" onClick={handleStart} disabled={!canStart} loading={busy}>Start</Button>
        </div>
      </div>

      {preview?.rows?.length > 0 && (
        <div className="border border-border rounded-md overflow-hidden">
          <div className="bg-muted px-3 py-2 text-xs text-muted-foreground">Preview (first {preview.rows.length} rows)</div>
          <div className="overflow-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr>
                  {preview.headers.map((h) => (
                    <th key={h} className="text-left p-2 border-b border-border bg-card sticky top-0">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r, idx) => (
                  <tr key={idx} className="border-b border-border">
                    {preview.headers.map((h) => (
                      <td key={h} className="p-2 align-top">{r[h]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="text-xs text-muted-foreground">
        <div>Processed: {progress.processed} / {progress.total} • Skipped: {progress.skipped} {progress.done ? '• Done' : ''}</div>
      </div>
    </div>
  );
};

export default UploadCsvCard;

